<template>
  <div class="flex flex-col min-h-screen items-center">
    <div class="flex justify-between items-center w-full max-w-[1280px]">
      <div class="flex">
        <PopupUserMenu :user="user" :active-item="'oauth-management'" />
        <LogoIcon />
      </div>
      <DropdownMenu />
    </div>
    <div class="flex flex-grow max-w-[1280px] mt-[60px] mx-auto w-full">
      <UserMenu :user="user" :active-item="'oauth-management'" />
      <div class="flex-1 px-6">
        <div v-if="loading" class="text-gray-500">Loading...</div>
        <div v-else-if="error" class="text-red-500">{{ error }}</div>
        <div v-else-if="app">
          <div class="flex justify-between items-start mb-6">
            <div>
              <router-link :to="{ name: 'OAuthManagementPage' }" class="text-blue-600 hover:underline text-sm">
                ← Back to list
              </router-link>
              <h1 class="text-2xl font-semibold mt-2">{{ app.name }}</h1>
            </div>
            <div v-if="app.status === 'active'" class="flex gap-3">
              <button
                class="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors text-sm"
                @click="showEditDialog = true"
              >
                Edit
              </button>
              <button
                class="px-4 py-2 rounded-lg border border-orange-500 text-orange-600 hover:bg-orange-50 transition-colors text-sm"
                @click="showRegenDialog = true"
              >
                Regenerate Credential
              </button>
              <button
                class="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors text-sm"
                @click="showRevokeDialog = true"
              >
                Revoke
              </button>
            </div>
          </div>

          <div v-if="app.status === 'revoked'" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-red-700">
            This application has been revoked.
          </div>

          <div v-if="app.status !== 'revoked' && app.secret_prev_expires_at && new Date(app.secret_prev_expires_at + 'Z') > new Date()" class="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6 text-orange-700 text-sm">
            Previous secret still valid until: <strong>{{ formatDate(app.secret_prev_expires_at) }}</strong>
          </div>

          <div class="bg-white border border-gray-200 rounded-xl p-6 space-y-4">
            <div class="grid grid-cols-3 gap-4 py-3 border-b border-gray-100">
              <span class="text-sm font-medium text-gray-500">Client ID (uid)</span>
              <span class="col-span-2 text-sm">{{ app.uid }}</span>
            </div>
            <div class="grid grid-cols-3 gap-4 py-3 border-b border-gray-100">
              <span class="text-sm font-medium text-gray-500">Client Secret</span>
              <span class="col-span-2 flex items-center gap-3">
                <span class="text-sm">{{ revealedSecret || app.secret }}</span>
                <button
                  v-if="!revealedSecret"
                  class="text-xs text-blue-600 hover:underline"
                  @click="showRevealDialog = true"
                >
                  Show Secret
                </button>
              </span>
            </div>
            <div class="grid grid-cols-3 gap-4 py-3 border-b border-gray-100">
              <span class="text-sm font-medium text-gray-500">Status</span>
              <span class="col-span-2">
                <span
                  :class="app.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
                  class="px-2 py-1 rounded-full text-xs font-semibold uppercase"
                >
                  {{ app.status }}
                </span>
              </span>
            </div>
            <div class="grid grid-cols-3 gap-4 py-3 border-b border-gray-100">
              <span class="text-sm font-medium text-gray-500">Login Redirect URIs</span>
              <div class="col-span-2 text-sm space-y-1">
                <div v-for="uri in app.redirect_uri" :key="uri">{{ uri }}</div>
              </div>
            </div>
            <div class="grid grid-cols-3 gap-4 py-3 border-b border-gray-100">
              <span class="text-sm font-medium text-gray-500">Logout Redirect URIs</span>
              <div class="col-span-2 text-sm space-y-1">
                <div v-if="app.logout_redirect_uri && app.logout_redirect_uri.length">
                  <div v-for="uri in app.logout_redirect_uri" :key="uri">{{ uri }}</div>
                </div>
                <span v-else class="text-gray-400">—（無）</span>
              </div>
            </div>
            <div class="grid grid-cols-3 gap-4 py-3 border-b border-gray-100">
              <span class="text-sm font-medium text-gray-500">Bypass Patterns</span>
              <div class="col-span-2 text-sm space-y-1">
                <div v-if="app.redirect_uri_bypass_patterns && app.redirect_uri_bypass_patterns.length">
                  <div v-for="p in app.redirect_uri_bypass_patterns" :key="p">{{ p }}</div>
                </div>
                <span v-else class="text-gray-400">—（無）</span>
              </div>
            </div>
            <div class="grid grid-cols-3 gap-4 py-3 border-b border-gray-100">
              <span class="text-sm font-medium text-gray-500">Creator</span>
              <span class="col-span-2 text-sm">{{ app.creator_email || '—' }}</span>
            </div>
            <div class="grid grid-cols-3 gap-4 py-3 border-b border-gray-100">
              <span class="text-sm font-medium text-gray-500">Created At</span>
              <span class="col-span-2 text-sm">{{ formatDate(app.created_at) }}</span>
            </div>
            <div v-if="app.revoker_email" class="grid grid-cols-3 gap-4 py-3 border-b border-gray-100">
              <span class="text-sm font-medium text-gray-500">Revoked By</span>
              <span class="col-span-2 text-sm">{{ app.revoker_email }} at {{ formatDate(app.revoked_at) }}</span>
            </div>
            <div class="grid grid-cols-3 gap-4 py-3">
              <span class="text-sm font-medium text-gray-500">Remark</span>
              <span class="col-span-2 text-sm">{{ app.remark || '—' }}</span>
            </div>
          </div>
        </div>

        <!-- Reveal Secret Dialog -->
        <ConfirmDialog
          v-if="showRevealDialog"
          title="Show Client Secret"
          message="Are you sure you want to reveal the client secret? This sensitive value should not be shared unnecessarily."
          confirm-label="Show Secret"
          @confirm="revealSecret"
          @cancel="showRevealDialog = false"
        />

        <!-- Revoke Dialog -->
        <ConfirmDialog
          v-if="showRevokeDialog"
          title="Revoke Application"
          message="This action is permanent and cannot be undone. The application's credentials will immediately stop working in all OAuth flows."
          confirm-label="Revoke"
          confirm-class="bg-red-600 hover:bg-red-700"
          @confirm="revokeApp"
          @cancel="showRevokeDialog = false"
        />

        <!-- Regenerate Credential Dialog -->
        <div v-if="showRegenDialog" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div class="bg-white rounded-xl shadow-xl p-8 w-full max-w-md">
            <h2 class="text-xl font-semibold mb-4">Regenerate Client Secret</h2>
            <div v-if="app.secret_prev_expires_at && new Date(app.secret_prev_expires_at + 'Z') > new Date()" class="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-red-700 text-sm">
              ⚠️ A secret is still in the buffer period (valid until {{ formatDate(app.secret_prev_expires_at) }}). Regenerating now will immediately invalidate it.
            </div>
            <p class="text-sm text-gray-600 mb-4">
              The old secret will remain valid for 72 hours. Please update all integrations within this period.
            </p>
            <div class="mb-4">
              <label for="regen-reason" class="block text-sm font-medium text-gray-700 mb-1">Reason (optional)</label>
              <textarea id="regen-reason" v-model="regenReason" rows="2" class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div class="flex gap-3 justify-end">
              <button class="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 text-sm" @click="showRegenDialog = false">Cancel</button>
              <button class="px-4 py-2 rounded-lg bg-orange-600 text-white hover:bg-orange-700 text-sm" @click="regenerateCredential">Regenerate</button>
            </div>
          </div>
        </div>

        <!-- New Secret Display -->
        <div v-if="newSecret" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div class="bg-white rounded-xl shadow-xl p-8 w-full max-w-md">
            <h2 class="text-xl font-semibold mb-4">New Client Secret</h2>
            <p class="text-sm text-orange-600 mb-3">⚠️ This is shown only once. Copy it now.</p>
            <div class="bg-gray-100 rounded-lg p-4 font-mono text-sm break-all mb-6">{{ newSecret }}</div>
            <button class="w-full px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700" @click="newSecret = null; revealedSecret = null; fetchApp()">Done</button>
          </div>
        </div>

        <!-- Edit Dialog -->
        <OAuthApplicationForm
          v-if="showEditDialog"
          mode="edit"
          :initial-data="app"
          @close="showEditDialog = false"
          @saved="onEdited"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { useAuthStore } from '@/stores/auth';
import { formatDateWithTimezone } from '@/utils/dateUtils';
import LogoIcon from '@/components/LogoIcon.vue';
import DropdownMenu from '@/components/DropdownMenu.vue';
import UserMenu from '@/components/UserMenu.vue';
import PopupUserMenu from '@/components/PopupUserMenu.vue';
import OAuthApplicationForm from '@/components/OAuthApplicationForm.vue';
import ConfirmDialog from '@/components/OAuthConfirmDialog.vue';
import axios from 'axios';

export default {
  name: 'OAuthApplicationDetailPage',
  components: { PopupUserMenu, LogoIcon, DropdownMenu, UserMenu, OAuthApplicationForm, ConfirmDialog },
  data() {
    return {
      app: null,
      loading: false,
      error: null,
      revealedSecret: null,
      newSecret: null,
      regenReason: '',
      showRevealDialog: false,
      showRevokeDialog: false,
      showRegenDialog: false,
      showEditDialog: false,
    };
  },
  computed: {
    user() {
      return useAuthStore().current_user || {};
    },
  },
  mounted() {
    this.fetchApp();
  },
  methods: {
    async fetchApp() {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await axios.get(`/api/oauth-management/applications/${this.$route.params.id}`);
        this.app = data;
      } catch (e) {
        this.error = e.response?.data?.detail || 'Failed to load application.';
      } finally {
        this.loading = false;
      }
    },
    formatDate: formatDateWithTimezone,
    async revealSecret() {
      this.showRevealDialog = false;
      try {
        const { data } = await axios.post(`/api/oauth-management/applications/${this.app.id}/reveal-secret`);
        this.revealedSecret = data.secret;
      } catch (e) {
        this.error = e.response?.data?.detail || 'Failed to reveal secret.';
      }
    },
    async revokeApp() {
      this.showRevokeDialog = false;
      try {
        await axios.post(`/api/oauth-management/applications/${this.app.id}/revoke`);
        this.fetchApp();
      } catch (e) {
        this.error = e.response?.data?.detail || 'Revoke failed.';
      }
    },
    async regenerateCredential() {
      this.showRegenDialog = false;
      try {
        const { data } = await axios.post(`/api/oauth-management/applications/${this.app.id}/regenerate-credential`, { reason: this.regenReason });
        this.newSecret = data.new_secret;
        this.regenReason = '';
      } catch (e) {
        this.error = e.response?.data?.detail || 'Regenerate failed.';
      }
    },
    onEdited() {
      this.showEditDialog = false;
      this.fetchApp();
    },
  },
};
</script>
